/* eslint-disable */
import {
  UserContext,
  UserSourceProvider,
  getIsTravelApp,
  userContextDefaultValues,
} from "@capone/common";
import { datadogRum } from "@datadog/browser-rum";
import {
  StylesProvider,
  ThemeProvider,
  createGenerateClassName,
} from "@material-ui/core/styles";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import {
  BrowserRouter,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  matchPath,
} from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";
import {
  CallState,
  CorpBusinessWithoutRewardsAccount,
  ExperimentStateWithCallState,
  MODAL_ALERT,
  RewardsAccount,
  SessionInfo,
  defaultVesperColors,
  installColorConfig,
} from "redmond";

import {
  ExperimentsHookProvider,
  useExperimentsById,
} from "@capone/experiments";
import { LoadScript } from "@react-google-maps/api";
import * as Portal from "@vesper/portal";
import clsx from "clsx";
import {
  CORPORATE_POST_MIGRATION_NOTICE_KEY,
  CORPORATE_SPARK_POST_MIGRATION_NOTICE_KEY,
  officialSparkMigrationLaunchDate,
  officialVxbMigrationLaunchDate,
  useDeviceTypes,
} from "halifax";

import "halifax/build/assets/style.css";
import "@vesper/portal/style.css";
import { fetchRewardsAccounts } from "../api/v1/rewards/fetchRewardsAccounts";
import { fetchRewardsAccountsV1 } from "../api/v1/rewards/fetchRewardsAccountsV1";
import { trackEvent } from "../api/v1/trackEvent";
import {
  setRewardsAccountsInStore,
  setSelectedAccountReferenceIdInStore,
} from "../app/actions";
import CapOneLogo from "../assets/b2b/capone-logo.svg";
import CapOneLoadingLogo from "../assets/b2b/ftux/capone-icon.svg";
import AppLoadingScreen from "../components/AppLoadingScreen";
import { BasicAuth, JWTAuth, ProtectedRoute } from "../components/Auth";
import AuthModule from "../components/AuthModule";
import AxiosInterceptors from "../components/AxiosInterceptors";
import Body from "../components/Body";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { FunnelEntryTabs } from "../components/Header/components/FunnelEntryTabs";
import { HomepageTakeover } from "../components/HomepageTakeover";
import Maintenance from "../components/Maintenance/maintenance";
import { getMaintenanceProps } from "../components/Maintenance/getMaintenanceProps";
import { PXCloudScript } from "../components/PXCloudScript";
import { RootBanner } from "../components/RootBanner/component";
import { SmartAppBanner } from "../components/SmartAppBanner";
import ExperimentsProvider, {
  AVAILABLE,
  CORE_ACTIVITIES,
  CORE_HOMEPAGE,
  CORE_VACATION_RENTALS_SHOP,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  TRAVEL_APP_GTM_EXPERIMENT,
  getExperimentVariant,
} from "../context/experiments";
import "../fonts.scss";
import { usePathname } from "../hooks/usePathname";
import { usePreAuthMaintenance } from "../hooks/usePreAuthMaintenance";
import { useWindowSize } from "../hooks/useWindowSize";
import config from "../utils/config";
import { CAP_ONE_LOGO_ALT } from "../utils/constants";
import {
  isActivitiesFunnelMobilePath,
  isActivitiesFunnelPath,
  isCarsFunnelPath,
  isFlightsFunnelPath,
  isHomepageEntryPath,
  isHotelsFunnelPath,
  isPackagesFunnelPath,
  isPremiumOffersFunnelPath,
  isPremiumStaysFunnelPath,
  isStaysFunnelPath,
  isVacationRentalsShopFunnelPath,
  shouldResolveHomepageDecision,
} from "../utils/homepageEntryPaths";
import {
  CAPONE_HEADER_UPDATED_STYLING_PATHS,
  DISPLAY_HOMEPAGE_TAKEOVER_PATHS,
  HIDDEN_BANNER_UNPROTECTED_PATHS,
  HIDDEN_FOOTER_PATHS,
  HIDDEN_FOOTER_PATHS_MOBILE,
  HIDDEN_FOOTER_UNPROTECTED_PATHS,
  HIDDEN_HEADER_PATHS_DESKTOP,
  HIDDEN_HEADER_PATHS_MOBILE,
  HIDDEN_HEADER_PATHS_MOBILE_GLOBAL_NAV,
  HIDDEN_HEADER_UNPROTECTED_PATHS,
  NO_FOOTER_PADDING_PATHS,
  NO_FOOTER_PADDING_UNPROTECTED_PATHS,
  PATH_ACTIVITIES_AVAILABILITY,
  PATH_AUTH,
  PATH_CARS_AVAILABILITY,
  PATH_CARS_SHOP,
  PATH_EXCHANGE_FLIGHT,
  PATH_EXCHANGE_FLIGHT_SHOP,
  PATH_FAVORITES,
  PATH_FLIGHTS_SHOP,
  PATH_HOME,
  PATH_HOTELS_ANCILLARY,
  PATH_HOTELS_ANCILLARY_CUSTOMIZE,
  PATH_HOTELS_AVAILABILITY,
  PATH_HOTELS_SHOP,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGE_REVIEW,
  PATH_STAYS_AVAILABILITY,
  PATH_WEBVIEW_PRELOAD,
  UNPROTECTED_PATHS,
  isCoreHomepageBookingConfirmationPath,
  pathsListContainsPath,
} from "../utils/urlPaths";
import { useFetchBusinessEligibility } from "../utils/useFetchBusinessEligibility";
import "./App.css";
import { vesperTheme } from "./theme";

const generateClassName = createGenerateClassName({
  productionPrefix: "ptBaseModule",
  seed: "ptBaseModule",
});

const DEFAULT_LOCALE = "en";

export const StyleContext = createContext({});

type PortalGateStatus = "unknown" | "eligible" | "ineligible";

const setViewWidthAndHeight = (width: string, height: string) => {
  document.body.style.setProperty(`--vw`, width);
  document.body.style.setProperty(`--vh`, height);
};

const HIDE_REWARDS_BANNER_ON_SCROLL_PATHS = [
  PATH_HOTELS_AVAILABILITY,
  PATH_STAYS_AVAILABILITY,
  PATH_FAVORITES,
  PATH_HOTELS_SHOP,
  PATH_HOTELS_ANCILLARY,
  PATH_HOTELS_ANCILLARY_CUSTOMIZE,
  PATH_CARS_AVAILABILITY,
  PATH_CARS_SHOP,
  PATH_FLIGHTS_SHOP,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_PACKAGE_REVIEW,
  PATH_ACTIVITIES_AVAILABILITY,
  PATH_EXCHANGE_FLIGHT,
  `${PATH_EXCHANGE_FLIGHT}/`,
  PATH_EXCHANGE_FLIGHT_SHOP,
  `${PATH_EXCHANGE_FLIGHT_SHOP}/`,
];

const VesperPortalHomepageHeaderV2 = (Portal as any)
  .VesperPortalHomepageHeaderV2 as React.ComponentType | undefined;
const CORE_HOMEPAGE_HEADER_ROUTE_REGEX = {
  faq: /^\/faq(?:\/|$)/,
  profile: /^\/profile(?:\/|$)/,
  terms: /^\/(terms-of-service|terms|site-terms-of-use)(?:\/|$)/,
  trips: /^\/trips(?:\/|$)/,
  tripsDisruption: /^\/trips\/disruption(?:\/|$)/,
  travelOffers: /^\/travel-offers(?:\/|$)/,
  travelWallet: /^\/travel-wallet(?:\/|$)/,
  premiumStaysVibes: /^\/premium-stays\/(vibes)?\/?$/,
  premiumOffers: /^\/premium-offers(?:\/|$)/,
};

/**
 * Gates the `@vesper/portal` bundle behind login, mirroring capone's
 * `BaseProviders` (components/Core/BaseProvider). `Portal.VesperPortal` mounts
 * `@vesper/portal`'s provider tree (its RTK store + router), and that bundle's
 * `useGetUserQuery`/`useGetExperiments` hooks fire on mount — so mounting it
 * before a session exists issues authenticated requests that 401 and kick the
 * SPA into the OAuth code flow (login/code → /oauth2/authorize) mid-login.
 *
 * Until the user is signed in we render only a bare router (like BaseProviders'
 * fallback) so the assertion/JWT auth flow can run without mounting the portal.
 * Once `sessionInfo.csrfToken` is set (post-login), we mount the real portal,
 * which supplies its own router + store, and its queries fire with a session.
 */
const VesperPortalGate = ({
  isLoggedIn,
  children,
}: {
  isLoggedIn: boolean;
  children: React.ReactNode;
}) => {
  if (isLoggedIn) {
    return <Portal.VesperPortal>{children}</Portal.VesperPortal>;
  }
  return (
    <BrowserRouter>
      <CompatRouter>{children}</CompatRouter>
    </BrowserRouter>
  );
};

const App = () => {
  const [sessionInfo, setSessionInfo] = useState({
    userInfo: { firstName: "", lastName: "", email: "" },
    isFirstSession: false,
    csrfToken: "",
    sessionExpiration: "",
    isDelegatedSession: "",
    userId: "",
  } as SessionInfo);
  const [rewardAccounts, setRewardAccounts] = useState<RewardsAccount[]>([]);
  const [isRewardsDisabled, setIsRewardsDisabled] = useState(false);
  const [selectedRewardsAccountId, setSelectedRewardsAccountId] = useState<
    string | null
  >(null);
  const [isCoreHomepageHeaderScrolled, setIsCoreHomepageHeaderScrolled] =
    useState(false);
  const [businessEligibilityStatus, setBusinessEligibilityStatus] =
    useState<PortalGateStatus>("unknown");
  const [eligibleBusinessAccounts, setEligibleBusinessAccounts] = useState<
    RewardsAccount[]
  >([]);
  const [businesses, setBusinesses] = useState<
    CorpBusinessWithoutRewardsAccount[]
  >([]);
  const [experimentState, setExperimentState] =
    useState<ExperimentStateWithCallState>();

  const updateSessionInfo = useCallback((sessionInfo: SessionInfo) => {
    setSessionInfo(sessionInfo);
    datadogRum.setUser({ id: sessionInfo.userId });
  }, []);

  const { isTravelApp } = getIsTravelApp();

  const { matchesMobile } = useDeviceTypes();

  useEffect(() => {
    datadogRum.setGlobalContextProperty(
      "channel",
      isTravelApp ? "TravelApp" : matchesMobile ? "Mobile" : "Web"
    );
  }, [isTravelApp, matchesMobile]);

  const updateBusinessEligibility = useCallback((eligible: boolean | null) => {
    if (eligible === null) {
      setBusinessEligibilityStatus("unknown");
      return;
    }

    setBusinessEligibilityStatus(eligible ? "eligible" : "ineligible");
  }, []);

  const windowSize = useWindowSize();
  setViewWidthAndHeight(
    `${windowSize.width * 0.01}px`,
    `${windowSize.height * 0.01}px`
  );

  const pathname = usePathname();
  const dispatch = useDispatch();
  const [launchEventSent, setLaunchEventSent] = useState(false);
  const [fetchRewardsAccountsCallState, setFetchRewardsAccountsCallState] =
    useState<CallState>(CallState.NotCalled);
  const [showCardMigrationNotice, setShowCardMigrationNotice] = useState(false);

  const isBusinessEligible = businessEligibilityStatus === "eligible";

  const isCorpSparkMigrationNoticeEnabled =
    useExperimentsById("c1-corporate-spark-migration-banner")?.variant ===
    "available";

  const isCorpSparkMigrationNoticeDebugEnabled =
    useExperimentsById("c1-corporate-spark-migration-banner")?.variant ===
    "debug";

  const isCorpSparkMigrationNoticeTestDateEnabled =
    useExperimentsById("c1-corporate-spark-migration-banner")?.variant ===
    "test-launch-date";

  const userContext = useMemo(
    () => ({
      ...userContextDefaultValues,
      sessionInfo,
      updateSessionInfo,
      isBusinessEligible,
      updateBusinessEligibility,
      eligibleBusinessAccounts,
      setEligibleBusinessAccounts,
      businesses,
      setBusinesses,
      logo: {
        src: CapOneLogo,
        alt: CAP_ONE_LOGO_ALT,
      },
      isRewardsDisabled,
    }),
    [
      businesses,
      eligibleBusinessAccounts,
      isBusinessEligible,
      sessionInfo,
      updateBusinessEligibility,
      updateSessionInfo,
      isRewardsDisabled,
    ]
  );

  const isGlobalMobileNavExperiment =
    getExperimentVariant(
      experimentState?.experiments || [],
      GLOBAL_MOBILE_NAV_EXPERIMENT
    ) === AVAILABLE;

  const coreHomepageVariant = getExperimentVariant(
    experimentState?.experiments || [],
    CORE_HOMEPAGE
  );

  const isCoreHomepageExperimentAvailable =
    coreHomepageVariant.startsWith(AVAILABLE);

  const showCoreHomepage =
    isBusinessEligible || isCoreHomepageExperimentAvailable;

  const isCoreActivitiesAvailable = getExperimentVariant(
    experimentState?.experiments || [],
    CORE_ACTIVITIES
  ).startsWith(AVAILABLE);

  const isCoreVacationRentalsShopActive = getExperimentVariant(
    experimentState?.experiments || [],
    CORE_VACATION_RENTALS_SHOP
  ).startsWith(AVAILABLE);

  const isTravelAppGTMEnabled =
    getExperimentVariant(
      experimentState?.experiments || [],
      TRAVEL_APP_GTM_EXPERIMENT
    ) === AVAILABLE;

  useEffect(() => {
    installColorConfig(defaultVesperColors);
    document.body.classList.add("xmod");
    document.body.classList.add("vesper");
  }, []);

  useEffect(() => {
    if (!launchEventSent && sessionInfo?.csrfToken) {
      setFetchRewardsAccountsCallState(CallState.InProcess);
      const fetchRewards = async () => {
        try {
          const rewardsAccounts = await fetchRewardsAccounts();

          setRewardAccounts(rewardsAccounts);
          dispatch(setRewardsAccountsInStore(rewardsAccounts));
          setFetchRewardsAccountsCallState(CallState.Success);
        } catch (err) {
          setFetchRewardsAccountsCallState(CallState.Failed);
        }
      };
      fetchRewards();
    }
  }, [launchEventSent, sessionInfo?.csrfToken]);

  useEffect(() => {
    if (!launchEventSent && sessionInfo?.csrfToken) {
      const fetchIsRewardsDisable = async () => {
        try {
          const { isRewardsDisabled: rewardsDisabled } =
            await fetchRewardsAccountsV1();
          setIsRewardsDisabled(rewardsDisabled);
        } catch (err) {
          // isRewardsDisabled is banner-only styling; leave the last known value on failure.
        }
      };
      fetchIsRewardsDisable();
    }
  }, [launchEventSent, sessionInfo?.csrfToken]);

  useEffect(() => {
    if (rewardAccounts.length) {
      const id = rewardAccounts[0]?.accountReferenceId;
      setSelectedRewardsAccountId(id);
      dispatch(setSelectedAccountReferenceIdInStore(id));
      localStorage.setItem("referenceId", id);
    }
  }, [rewardAccounts]);

  useEffect(() => {
    if (window.location.search.includes("fromCorporate=true")) {
      localStorage.setItem("fromCorporate", "true");
    }
  }, []);

  useEffect(() => {
    const preloadImg = new Image();
    preloadImg.src = CapOneLoadingLogo;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsCoreHomepageHeaderScrolled(window.scrollY > 0);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isCorpSparkMigrationNoticeDebugEnabled) {
      setShowCardMigrationNotice(true);
      return;
    }

    let sparkMigrationLaunchDate: Date;

    if (isCorpSparkMigrationNoticeTestDateEnabled) {
      const params = new URLSearchParams(window.location.search);
      const dateParam = params.get("launchTestDate");

      sparkMigrationLaunchDate = dateParam
        ? new Date(dateParam)
        : officialSparkMigrationLaunchDate;
    } else {
      sparkMigrationLaunchDate = officialSparkMigrationLaunchDate;
    }

    const maybeSparkCardBusiness = businesses.some((business) => {
      const businessCreationDate = business.optedIn?.createdAt;
      if (!businessCreationDate) {
        return false;
      }

      return (
        business.productDisplayName.toLowerCase().includes("spark") &&
        new Date(businessCreationDate) >= sparkMigrationLaunchDate
      );
    });

    const shouldShowNoticeForSparkCard =
      (isCorpSparkMigrationNoticeTestDateEnabled ||
        isCorpSparkMigrationNoticeEnabled) &&
      localStorage.getItem(CORPORATE_SPARK_POST_MIGRATION_NOTICE_KEY) !==
        "true" &&
      maybeSparkCardBusiness;

    let vxbMigrationLaunchDate: Date;

    if (isCorpSparkMigrationNoticeTestDateEnabled) {
      const params = new URLSearchParams(window.location.search);
      const dateParam = params.get("launchTestDate");

      vxbMigrationLaunchDate = dateParam
        ? new Date(dateParam)
        : officialVxbMigrationLaunchDate;
    } else {
      vxbMigrationLaunchDate = officialVxbMigrationLaunchDate;
    }

    const maybeVxbCardBusiness = businesses.some((business) => {
      const businessCreationDate = business.optedIn?.createdAt;
      if (!businessCreationDate) {
        return false;
      }

      return (
        business.productDisplayName.includes("Venture X Business") &&
        new Date(businessCreationDate) >= vxbMigrationLaunchDate
      );
    });

    const shouldShowNoticeForVxbCard =
      localStorage.getItem(CORPORATE_POST_MIGRATION_NOTICE_KEY) !== "true" &&
      maybeVxbCardBusiness;

    if (shouldShowNoticeForVxbCard || shouldShowNoticeForSparkCard) {
      trackEvent({
        eventName: MODAL_ALERT,
        properties: {
          type: "card_migration_helptip",
          primary_button: "got_it",
          secondary_button: "view_business_travel",
          rewards_accounts: businesses
            .map((r) => r.productDisplayName)
            .join(","),
        },
      });
      setShowCardMigrationNotice(true);
    }
  }, [
    isCorpSparkMigrationNoticeEnabled,
    eligibleBusinessAccounts,
    businesses,
    isCorpSparkMigrationNoticeDebugEnabled,
    isCorpSparkMigrationNoticeTestDateEnabled,
  ]);

  const isInMaintenance = (config: {
    isMaintenanceModeEnabled: any;
    isTimedMaintenanceModeEnabled: any;
    MAINTENANCE_START_TIME?: number;
    MAINTENANCE_END_TIME?: number;
  }) => {
    if (
      config.isTimedMaintenanceModeEnabled === "true" &&
      config.MAINTENANCE_START_TIME &&
      config.MAINTENANCE_END_TIME
    ) {
      const currentTime = Date.now();
      return (
        currentTime <= config.MAINTENANCE_END_TIME &&
        currentTime >= config.MAINTENANCE_START_TIME
      );
    }
    return config.isMaintenanceModeEnabled === "true";
  };

  const hideDesktopHeader = (pathname: string) =>
    pathsListContainsPath(HIDDEN_HEADER_PATHS_DESKTOP, pathname) ||
    isHotelsFunnelPath(pathname) ||
    isStaysFunnelPath(pathname) ||
    isPremiumStaysFunnelPath(pathname) ||
    isPremiumOffersFunnelPath(pathname) ||
    (showCoreHomepage && isHomepageEntryPath(pathname));
  const isCoreHomepageHeaderPath = (pathname: string) =>
    showCoreHomepage &&
    !CORE_HOMEPAGE_HEADER_ROUTE_REGEX.tripsDisruption.test(pathname) &&
    (CORE_HOMEPAGE_HEADER_ROUTE_REGEX.faq.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.profile.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.terms.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.travelOffers.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.travelWallet.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.trips.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.premiumStaysVibes.test(pathname) ||
      CORE_HOMEPAGE_HEADER_ROUTE_REGEX.premiumOffers.test(pathname) ||
      (!isCoreActivitiesAvailable &&
        !matchesMobile &&
        isActivitiesFunnelPath(pathname)));
  const isCoreHomepageBookingConfirmationHeaderPath = (pathname: string) =>
    showCoreHomepage && isCoreHomepageBookingConfirmationPath(pathname);
  const usesCoreHomepageHeader = (pathname: string) =>
    isCoreHomepageHeaderPath(pathname) ||
    isCoreHomepageBookingConfirmationHeaderPath(pathname);
  const shouldFetchHomepageDecision = shouldResolveHomepageDecision(pathname);
  const isHomepageExperimentResolved =
    !shouldFetchHomepageDecision ||
    !sessionInfo.csrfToken ||
    experimentState?.callState === CallState.Success;

  const isStaysPaths = useCallback(
    (location: { pathname: string | string[] }) => {
      const staysPaths = [
        "/stays/availability",
        "/stays/shop",
        "/stays/ancillary",
        "/stays/favorites",
      ];
      return staysPaths.some((path) => location.pathname.includes(path));
    },
    []
  );

  const { isResolved: isBusinessEligibilityResolved } =
    useFetchBusinessEligibility({
      csrfToken: sessionInfo.csrfToken,
      setEligibleBusinessAccounts,
      setBusinesses,
      updateBusinessEligibility,
      enabled: shouldFetchHomepageDecision,
    });
  const isHomepageDecisionResolved =
    !shouldFetchHomepageDecision ||
    !sessionInfo.csrfToken ||
    (isHomepageExperimentResolved &&
      (!isCoreHomepageExperimentAvailable || isBusinessEligibilityResolved)) ||
    (isBusinessEligibilityResolved && isBusinessEligible);

  const maintenanceModeConfig = useExperimentsById("cap1-portal-maintenance");

  const preAuthMaintenance = usePreAuthMaintenance();

  const maintenanceProps = getMaintenanceProps(
    preAuthMaintenance,
    maintenanceModeConfig?.data
  );

  const getCoreHomepageHeaderElement = (routePathname: string) => {
    if (
      !usesCoreHomepageHeader(routePathname) ||
      !VesperPortalHomepageHeaderV2 ||
      (isTravelApp && matchesMobile)
    ) {
      return null;
    }

    return <VesperPortalHomepageHeaderV2 />;
  };

  const shouldHideProtectedHeader = (routePathname: string) => {
    if (matchesMobile && !isTravelApp) {
      if (
        experimentState?.callState === CallState.Success &&
        isGlobalMobileNavExperiment
      ) {
        return (
          HIDDEN_HEADER_PATHS_MOBILE_GLOBAL_NAV.includes(routePathname) ||
          (showCoreHomepage &&
            (isHotelsFunnelPath(routePathname) ||
              isStaysFunnelPath(routePathname) ||
              isCarsFunnelPath(routePathname) ||
              isFlightsFunnelPath(routePathname) ||
              isPackagesFunnelPath(routePathname) ||
              isPremiumStaysFunnelPath(routePathname) ||
              (!isCoreActivitiesAvailable &&
                isActivitiesFunnelMobilePath(routePathname))))
        );
      }

      return HIDDEN_HEADER_PATHS_MOBILE.includes(routePathname);
    }

    return hideDesktopHeader(routePathname);
  };

  const getProtectedBodyClassName = (routePathname: string) =>
    clsx({
      "desktop-top-margin":
        !usesCoreHomepageHeader(routePathname) &&
        !hideDesktopHeader(routePathname) &&
        !matchesMobile,
    });

  const getUnprotectedAppContentClassName = (routePathname: string) =>
    clsx("App-content", {
      "no-padding": NO_FOOTER_PADDING_UNPROTECTED_PATHS.includes(routePathname),
      "core-homepage-header": usesCoreHomepageHeader(routePathname),
      "core-homepage-header-scrolled":
        usesCoreHomepageHeader(routePathname) && isCoreHomepageHeaderScrolled,
      mobile: matchesMobile,
    });

  const getUnprotectedBodyClassName = (routePathname: string) =>
    clsx({
      "desktop-top-margin":
        !matchesMobile &&
        !usesCoreHomepageHeader(routePathname) &&
        !matchPath(routePathname, HIDDEN_HEADER_UNPROTECTED_PATHS),
    });

  const renderLegacyHeader = (
    routePathname: string,
    notAuthenticated = false,
    className = ""
  ) => (
    <Header
      className={className}
      locationPath={routePathname}
      language={DEFAULT_LOCALE}
      rewardsAccounts={rewardAccounts}
      selectedRewardsAccountId={selectedRewardsAccountId}
      setSelectedRewardsAccountId={setSelectedRewardsAccountId}
      notAuthenticated={notAuthenticated}
      showCorpMigrationNotice={showCardMigrationNotice}
      corpBusinessAccounts={businesses}
    />
  );

  const renderUnprotectedHeader = (
    routePathname: string,
    notAuthenticated: boolean
  ) => {
    if (HIDDEN_HEADER_UNPROTECTED_PATHS.includes(routePathname)) {
      return null;
    }

    return (
      getCoreHomepageHeaderElement(routePathname) ||
      renderLegacyHeader(routePathname, notAuthenticated)
    );
  };

  return (
    <ExperimentsProvider
      isLoggedIn={!!sessionInfo.csrfToken}
      onStateChange={setExperimentState}
    >
      <ExperimentsHookProvider isLoggedIn={!!sessionInfo.csrfToken}>
        {isInMaintenance(config) ||
          maintenanceModeConfig?.variant === "page" ||
          preAuthMaintenance ? (
          <Maintenance
            title={maintenanceProps.title}
            subtext={maintenanceProps.subtext}
            icon={maintenanceProps.icon}
          />
        ) : (
          <LoadScript googleMapsApiKey={config.googleMapsApiKey || ""}>
            <VesperPortalGate isLoggedIn={!!sessionInfo.csrfToken}>
              <UserSourceProvider>
                <AxiosInterceptors />
                <StyleContext.Provider value={{ muiTheme: vesperTheme }}>
                  <StylesProvider generateClassName={generateClassName}>
                    <ThemeProvider theme={vesperTheme.theme}>
                      <JWTAuth>
                        <UserContext.Provider value={userContext}>
                          <PXCloudScript />
                          <div
                            className={clsx("App-container", {
                              "core-homepage-mobile":
                                showCoreHomepage && matchesMobile,
                            })}
                          >
                            <BasicAuth redirectUrl={PATH_HOME} />
                            <Switch>
                              {stripPathLeaf()}
                              <Route
                                path={PATH_AUTH}
                                render={(
                                  browserRouterProps: RouteComponentProps
                                ) => (
                                  <AuthModule
                                    language={DEFAULT_LOCALE}
                                    {...browserRouterProps}
                                  />
                                )}
                              />
                              <Route
                                path={PATH_WEBVIEW_PRELOAD}
                                render={() => <div />}
                              />
                              <Route path="*">
                                <ProtectedRoute
                                  protectedUrl={PATH_HOME}
                                  protectedContent={(pathname: string) => {
                                    const { isTravelApp } = getIsTravelApp();
                                    const isHomepageDecisionPending =
                                      !!sessionInfo.csrfToken &&
                                      shouldFetchHomepageDecision &&
                                      !isHomepageDecisionResolved;
                                    const shouldBlockOnHomepageDecision =
                                      isHomepageDecisionPending &&
                                      !isCoreHomepageBookingConfirmationPath(
                                        pathname
                                      );

                                    if (shouldBlockOnHomepageDecision) {
                                      return (
                                        <AppLoadingScreen
                                          logoSrc={CapOneLoadingLogo}
                                          logoAlt={CAP_ONE_LOGO_ALT}
                                        />
                                      );
                                    }

                                    return (
                                      <>
                                        {isTravelAppGTMEnabled &&
                                          !isTravelApp && <SmartAppBanner />}
                                        <div
                                          className={clsx("App-content", {
                                            "no-padding":
                                              NO_FOOTER_PADDING_PATHS.includes(
                                                pathname
                                              ) || isTravelApp,
                                            "core-homepage-header":
                                              usesCoreHomepageHeader(pathname),
                                            "core-homepage-header-scrolled":
                                              usesCoreHomepageHeader(
                                                pathname
                                              ) && isCoreHomepageHeaderScrolled,
                                          })}
                                        >
                                          <Route
                                            render={(props) => {
                                              const isCoreHomepageEntryPath =
                                                showCoreHomepage &&
                                                isHomepageEntryPath(
                                                  props.location.pathname
                                                );
                                              const isCoreHomepageBookingConfirmation =
                                                isCoreHomepageBookingConfirmationHeaderPath(
                                                  props.location.pathname
                                                );
                                              const coreHomepageHeader =
                                                getCoreHomepageHeaderElement(
                                                  props.location.pathname
                                                );
                                              const rootBanner = (
                                                <RootBanner
                                                  {...props}
                                                  hideOnScroll={
                                                    matchesMobile &&
                                                    isGlobalMobileNavExperiment &&
                                                    HIDE_REWARDS_BANNER_ON_SCROLL_PATHS.includes(
                                                      props.location.pathname
                                                    )
                                                  }
                                                  rewardsAccounts={
                                                    rewardAccounts
                                                  }
                                                  isRewardsDisabled={
                                                    isRewardsDisabled
                                                  }
                                                />
                                              );

                                              if (isCoreHomepageEntryPath) {
                                                return null;
                                              }
                                              if (coreHomepageHeader) {
                                                if (
                                                  isCoreHomepageBookingConfirmation ||
                                                  isActivitiesFunnelPath(
                                                    props.location.pathname
                                                  )
                                                ) {
                                                  return (
                                                    <>
                                                      {rootBanner}
                                                      <div className="core-homepage-confirmation-header-offset">
                                                        {coreHomepageHeader}
                                                      </div>
                                                    </>
                                                  );
                                                }
                                                return coreHomepageHeader;
                                              }
                                              const isHeaderHidden =
                                                shouldHideProtectedHeader(
                                                  props.location.pathname
                                                );
                                              const showMobileHomepageHeader =
                                                isHeaderHidden &&
                                                matchesMobile &&
                                                ((!isTravelApp &&
                                                  isPackagesFunnelPath(
                                                    props.location.pathname
                                                  )) ||
                                                  (!isTravelApp &&
                                                    isPremiumStaysFunnelPath(
                                                      props.location.pathname
                                                    ) &&
                                                    !(
                                                      isCoreVacationRentalsShopActive &&
                                                      isVacationRentalsShopFunnelPath(
                                                        props.location.pathname
                                                      )
                                                    )) ||
                                                  (!isTravelApp &&
                                                    !isCoreActivitiesAvailable &&
                                                    isActivitiesFunnelMobilePath(
                                                      props.location.pathname
                                                    ))) &&
                                                VesperPortalHomepageHeaderV2;
                                              if (isHeaderHidden) {
                                                return (
                                                  <>
                                                    {rootBanner}
                                                    {showMobileHomepageHeader ? (
                                                      <div className="core-homepage-mobile-funnel-header">
                                                        <VesperPortalHomepageHeaderV2 />
                                                      </div>
                                                    ) : null}
                                                  </>
                                                );
                                              }
                                              return (
                                                (!isTravelApp ||
                                                  !matchesMobile) && (
                                                  <>
                                                    {rootBanner}
                                                    {renderLegacyHeader(
                                                      pathname,
                                                      false,
                                                      !CAPONE_HEADER_UPDATED_STYLING_PATHS.includes(
                                                        props.location.pathname
                                                      ) &&
                                                        !isStaysPaths(
                                                          props.location
                                                        )
                                                        ? "homepage"
                                                        : ""
                                                    )}
                                                  </>
                                                )
                                              );
                                            }}
                                          />
                                          <Route
                                            render={(props) => {
                                              if (
                                                !DISPLAY_HOMEPAGE_TAKEOVER_PATHS.includes(
                                                  props.location.pathname
                                                ) ||
                                                (showCoreHomepage &&
                                                  isHomepageEntryPath(
                                                    props.location.pathname
                                                  ))
                                              ) {
                                                return null;
                                              }
                                              if (matchesMobile) {
                                                return (
                                                  <>
                                                    <HomepageTakeover
                                                      {...props}
                                                      rewardsAccounts={
                                                        rewardAccounts
                                                      }
                                                    />
                                                    <FunnelEntryTabs
                                                      {...props}
                                                      isMobile
                                                    />
                                                  </>
                                                );
                                              }
                                              return (
                                                <HomepageTakeover
                                                  {...props}
                                                  rewardsAccounts={
                                                    rewardAccounts
                                                  }
                                                />
                                              );
                                            }}
                                          />
                                          <Body
                                            language={DEFAULT_LOCALE}
                                            isFirstSession={
                                              sessionInfo.isFirstSession
                                            }
                                            launchEventSent={launchEventSent}
                                            setLaunchEventSent={
                                              setLaunchEventSent
                                            }
                                            className={getProtectedBodyClassName(
                                              pathname
                                            )}
                                            rewardsAccounts={rewardAccounts}
                                            selectedRewardsAccountId={
                                              selectedRewardsAccountId
                                            }
                                            fetchRewardsAccountsCallState={
                                              fetchRewardsAccountsCallState
                                            }
                                            pathname={pathname}
                                            showCoreHomepage={showCoreHomepage}
                                            coreHomepageVariant={
                                              coreHomepageVariant
                                            }
                                            businessEligibilityStatus={
                                              businessEligibilityStatus
                                            }
                                          />
                                        </div>
                                        <Route
                                          render={(props) => {
                                            const { isTravelApp } =
                                              getIsTravelApp();
                                            if (
                                              (showCoreHomepage &&
                                                isHomepageEntryPath(
                                                  props.location.pathname
                                                )) ||
                                              HIDDEN_FOOTER_PATHS.includes(
                                                props.location.pathname
                                              ) ||
                                              (matchesMobile &&
                                                (HIDDEN_FOOTER_PATHS_MOBILE.includes(
                                                  props.location.pathname
                                                ) ||
                                                  isTravelApp))
                                            ) {
                                              return null;
                                            }
                                            return (
                                              <Footer
                                                pathname={
                                                  props.location.pathname
                                                }
                                              />
                                            );
                                          }}
                                        />
                                      </>
                                    );
                                  }}
                                  unprotectedContent={(
                                    notAuthenticated: boolean
                                  ) => (
                                    <Route
                                      path={UNPROTECTED_PATHS}
                                      render={(prop) => (
                                        <>
                                          <div
                                            className={getUnprotectedAppContentClassName(
                                              prop.location.pathname
                                            )}
                                          >
                                            <RootBanner
                                              isHidden={
                                                HIDDEN_BANNER_UNPROTECTED_PATHS.includes(
                                                  prop.location.pathname
                                                ) || notAuthenticated
                                              }
                                              rewardsAccounts={rewardAccounts}
                                              isRewardsDisabled={
                                                isRewardsDisabled
                                              }
                                              {...prop}
                                            />
                                            {renderUnprotectedHeader(
                                              prop.location.pathname,
                                              notAuthenticated
                                            )}
                                            <Body
                                              language={DEFAULT_LOCALE}
                                              isFirstSession={
                                                sessionInfo.isFirstSession
                                              }
                                              launchEventSent={launchEventSent}
                                              setLaunchEventSent={
                                                setLaunchEventSent
                                              }
                                              className={getUnprotectedBodyClassName(
                                                prop.location.pathname
                                              )}
                                              rewardsAccounts={rewardAccounts}
                                              selectedRewardsAccountId={
                                                selectedRewardsAccountId
                                              }
                                              fetchRewardsAccountsCallState={
                                                fetchRewardsAccountsCallState
                                              }
                                              pathname={prop.location.pathname}
                                              notAuthenticated={
                                                notAuthenticated
                                              }
                                              showCoreHomepage={
                                                showCoreHomepage
                                              }
                                              coreHomepageVariant={
                                                coreHomepageVariant
                                              }
                                              businessEligibilityStatus={
                                                businessEligibilityStatus
                                              }
                                            />
                                          </div>
                                          {!(
                                            (showCoreHomepage &&
                                              isHomepageEntryPath(
                                                prop.location.pathname
                                              )) ||
                                            HIDDEN_FOOTER_UNPROTECTED_PATHS.includes(
                                              prop.location.pathname
                                            )
                                          ) && <Footer />}
                                        </>
                                      )}
                                    />
                                  )}
                                />
                              </Route>
                            </Switch>
                          </div>
                        </UserContext.Provider>
                      </JWTAuth>
                    </ThemeProvider>
                  </StylesProvider>
                </StyleContext.Provider>
              </UserSourceProvider>
            </VesperPortalGate>
          </LoadScript>
        )}
      </ExperimentsHookProvider>
    </ExperimentsProvider>
  );
};

export default App;

const stripPathLeaf = () =>
  (
    <>
      {/* note: standardize the path to have EXACTLY ONE trailing slash WITHOUT index.html suffix */}
      <Route
        exact
        strict
        path="/:url*//*"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: `${props.location.pathname.replace(/\/+$/, "")}/`,
            }}
          />
        )}
      />
      <Route
        exact
        path="/:url*/index.html/"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: props.location.pathname.slice(0, -"index.html/".length),
            }}
          />
        )}
      />
      <Route
        exact
        path="/:url*/index.html"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: props.location.pathname.slice(0, -"index.html".length),
            }}
          />
        )}
      />
      <Route
        exact
        strict
        path="/:url*"
        render={(props: RouteComponentProps) => (
          <Redirect
            to={{
              ...props.location,
              pathname: `${props.location.pathname}/`,
            }}
          />
        )}
      />
    </>
  ).props.children;
